/**
 * GovRAMP product list parser — V105-1-3 / ADR-0075 §3. Pure: no network, no DB, no xlsx
 * library (the Worker parses the uploaded Excel export with SheetJS and hands the rows here).
 *
 * Input: the first sheet as rows of cells (`sheet_to_json(ws, { header: 1 })`).
 * Pinned header: Organization Name | Service Offering | Service Model | Impact Level | Status.
 * A header change, an unknown Status value, a repeated org + offering or a row count under
 * 90 % of the last applied upload is a HOLD: the file files no row proposals, one shape
 * review item instead (the ADR-0075 §1 rule). Extra columns are a note.
 *
 * The export has no stable id: the key is sha256(lower(trim(org)) | lower(trim(offering)))[0:16].
 */
import { canonicalJson, sha256Hex } from '../utils/audit-crypto.js';
export const GOVRAMP_PARSER_VERSION = '1.0.0';
export const GOVRAMP_SOURCE_ID = 'src-govramp-product-list';
export const GOVRAMP_FILE = 'govramp-product-list.xlsx';
export const GOVRAMP_HEADER = ['Organization Name', 'Service Offering', 'Service Model', 'Impact Level', 'Status'];
/**
 * Pinned Status vocabulary → the `external_services.govramp_status` values, plus `core`
 * (a GovRAMP tier listed on the APL since 2026; the tenant column has no `core`, so a link
 * shows the raw value). Keys are normalized: lower case, hyphens and runs of spaces → one space.
 */
export const GOVRAMP_STATUS = Object.freeze({
    authorized: 'authorized',
    'provisionally authorized': 'provisionally_authorized',
    provisional: 'provisionally_authorized',
    ready: 'ready',
    core: 'core',
    'progressing snapshot': 'progressing_snapshot',
    'one time snapshot': 'one_time_snapshot',
});
const IMPACT = Object.freeze({
    low: 'low', 'low+': 'low', moderate: 'moderate', high: 'high',
});
const cell = (v) => (v === null || v === undefined ? '' : String(v)).replace(/\s+/g, ' ').trim();
const norm = (v) => v.toLowerCase().replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
export async function govrampProductKey(org, offering) {
    return (await sha256Hex(`${org.trim().toLowerCase()}|${offering.trim().toLowerCase()}`)).slice(0, 16);
}
export async function parseGovrampProductList(input) {
    const hold = [];
    const notes = [];
    const want = GOVRAMP_HEADER.map((h) => norm(h));
    let headerRow = null;
    for (let i = 0; i < Math.min(input.rows.length, 25); i++) {
        const cells = (input.rows[i] ?? []).map(cell).map(norm);
        if (want.every((h, j) => cells[j] === h)) {
            headerRow = i;
            break;
        }
    }
    const observedHeader = (headerRow !== null ? input.rows[headerRow] : input.rows.find((r) => (r ?? []).some((c) => cell(c))) ?? []).map(cell);
    if (headerRow === null)
        hold.push(`pinned header not found (${GOVRAMP_HEADER.join(' | ')}); first row: ${observedHeader.slice(0, 8).join(' | ') || '(empty)'}`);
    else if (observedHeader.slice(GOVRAMP_HEADER.length).some(Boolean))
        notes.push(`extra columns: ${observedHeader.slice(GOVRAMP_HEADER.length).filter(Boolean).join(', ')}`);
    const products = [];
    const seen = new Set();
    if (headerRow !== null) {
        for (let i = headerRow + 1; i < input.rows.length; i++) {
            const c = (input.rows[i] ?? []).map(cell);
            if (!c.slice(0, 5).some(Boolean))
                continue; // blank row
            const [org, offering, model, level, statusRaw] = c;
            if (!org || !offering) {
                hold.push(`row ${i + 1}: Organization Name and Service Offering are required`);
                continue;
            }
            const status = GOVRAMP_STATUS[norm(statusRaw ?? '')];
            if (!status) {
                hold.push(`row ${i + 1}: Status value outside the pinned vocabulary: ${JSON.stringify(statusRaw ?? '')}`);
                continue;
            }
            const key = await govrampProductKey(org, offering);
            if (seen.has(key)) {
                hold.push(`row ${i + 1}: repeats ${org} / ${offering}`);
                continue;
            }
            seen.add(key);
            const row = {
                govramp_product_key: key, organization_name: org, service_offering: offering, service_model: model || null,
                impact_level: level || null, impact_rank: level ? IMPACT[norm(level)] ?? null : null, status_raw: statusRaw, govramp_status: status,
            };
            products.push({ ...row, row_sha256: await sha256Hex(canonicalJson(row)) });
        }
    }
    const prev = input.previous_rows ?? 0;
    if (prev > 0 && products.length < Math.ceil(prev * 0.9))
        hold.push(`row count ${products.length} is below 90% of the last applied upload (${prev})`);
    const holdSorted = [...new Set(hold)].sort();
    return {
        parser_version: GOVRAMP_PARSER_VERSION,
        status: holdSorted.length ? 'held' : notes.length ? 'noted' : 'ok',
        hold_reasons: holdSorted,
        notes,
        header_row: headerRow,
        observed_header: observedHeader,
        observed_shape_sha256: await sha256Hex(canonicalJson({ file: GOVRAMP_FILE, header: observedHeader.map(norm), hold: holdSorted })),
        rows: products.length,
        products: holdSorted.length ? [] : products,
    };
}
//# sourceMappingURL=govramp-product-list-parser.js.map